import { useState } from 'react'
import {
  Card,
  Steps,
  Button,
  Space,
  Radio,
  Select,
  Table,
  Tag,
  Alert,
  Progress,
  message,
} from 'antd'
import type { RadioChangeEvent, TableColumnsType } from 'antd'
import {
  RightOutlined,
  LeftOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons'
import { useQuery, useQueries } from '@tanstack/react-query'
import { usersApi } from '@/api/users'
import { hospitalsApi } from '@/api/hospitals'
import { permissionsApi } from '@/api/permissions'
import type { User } from '@/types/user'
import type { Hospital } from '@/types/hospital'

type OperationType = 'grant' | 'revoke' | 'modify'

interface PermissionLevel {
  admin: boolean
  sender: boolean
  receiver: boolean
  readonly: boolean
  bi: boolean
  dashboard: boolean
  teamInsights: boolean
}

interface BulkOperation {
  userId: string
  facilityId: string
  permissions?: PermissionLevel
}

export const BulkPermissions = () => {
  const [currentStep, setCurrentStep] = useState(0)
  const [operationType, setOperationType] = useState<OperationType>('grant')
  const [selectedUsers, setSelectedUsers] = useState<string[]>([])
  const [selectedHospitals, setSelectedHospitals] = useState<string[]>([])
  const [permissionLevels, setPermissionLevels] = useState<PermissionLevel>({
    admin: false,
    sender: false,
    receiver: false,
    readonly: false,
    bi: false,
    dashboard: false,
    teamInsights: false,
  })
  const [executing, setExecuting] = useState(false)
  const [progress, setProgress] = useState(0)
  const [completed, setCompleted] = useState(false)

  // User search
  const [userSearchTerm, setUserSearchTerm] = useState('')
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['users', { search: userSearchTerm }],
    queryFn: () => usersApi.getUsers({ search: userSearchTerm, limit: 100 }),
    enabled: userSearchTerm.length > 0,
  })

  // Hospital search
  const [hospitalSearchTerm, setHospitalSearchTerm] = useState('')
  const { data: hospitalsData, isLoading: hospitalsLoading } = useQuery({
    queryKey: ['hospitals-search', hospitalSearchTerm],
    queryFn: () => hospitalsApi.searchHospitals(hospitalSearchTerm),
    enabled: hospitalSearchTerm.length > 2,
  })

  // Fetch selected users' full details using individual queries
  const selectedUsersQueries = useQueries({
    queries: selectedUsers.map((userId) => ({
      queryKey: ['user', userId],
      queryFn: () => usersApi.getUser(userId),
      enabled: selectedUsers.length > 0,
    })),
  })

  const selectedUsersData = selectedUsersQueries
    .filter((query) => query.data?.data)
    .map((query) => query.data!.data)

  // Fetch selected hospitals' full details using bulk endpoint
  const { data: selectedHospitalsBulkData } = useQuery({
    queryKey: ['hospitals-bulk', selectedHospitals],
    queryFn: () => hospitalsApi.getBulkHospitals(selectedHospitals),
    enabled: selectedHospitals.length > 0,
  })

  const selectedHospitalsData = selectedHospitalsBulkData?.data || []

  const handleNext = () => {
    setCurrentStep(currentStep + 1)
  }

  const handlePrev = () => {
    setCurrentStep(currentStep - 1)
  }

  const handleExecute = async () => {
    setExecuting(true)
    setProgress(0)

    const operations: BulkOperation[] = []

    // Build operations list
    for (const userId of selectedUsers) {
      for (const facilityId of selectedHospitals) {
        operations.push({
          userId,
          facilityId,
          permissions: operationType !== 'revoke' ? permissionLevels : undefined,
        })
      }
    }

    const totalOperations = operations.length
    let completedOperations = 0

    try {
      // Execute operations one by one (could be optimized with batching)
      for (const operation of operations) {
        if (operationType === 'revoke') {
          await permissionsApi.revokeFacilityPermission(operation.userId, operation.facilityId)
        } else {
          // Find the hospital to get its cmsid
          const hospital = selectedHospitalsData.find(
            (h: Hospital) => h.id === operation.facilityId
          )
          if (!hospital) {
            throw new Error(`Hospital not found: ${operation.facilityId}`)
          }

          await permissionsApi.setFacilityPermission(operation.userId, operation.facilityId, {
            cmsid: hospital.cmsid,
            ...operation.permissions!,
          })
        }

        completedOperations++
        setProgress(Math.round((completedOperations / totalOperations) * 100))
      }

      setCompleted(true)
      message.success(
        `Successfully ${operationType === 'revoke' ? 'revoked' : 'granted'} ${totalOperations} permissions`
      )
    } catch (error) {
      message.error(`Failed to complete bulk operation: ${error}`)
    } finally {
      setExecuting(false)
    }
  }

  const handleReset = () => {
    setCurrentStep(0)
    setOperationType('grant')
    setSelectedUsers([])
    setSelectedHospitals([])
    setPermissionLevels({
      admin: false,
      sender: false,
      receiver: false,
      readonly: false,
      bi: false,
      dashboard: false,
      teamInsights: false,
    })
    setProgress(0)
    setCompleted(false)
  }

  // Step 1: Select Operation Type
  const renderOperationTypeStep = () => (
    <Card>
      <div style={{ marginBottom: 16 }}>
        <h3>Select Operation Type</h3>
        <p style={{ color: '#666' }}>
          Choose the type of bulk permission operation you want to perform
        </p>
      </div>
      <Radio.Group
        onChange={(e: RadioChangeEvent) => setOperationType(e.target.value)}
        value={operationType}
        style={{ width: '100%' }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Card
            hoverable
            style={{
              border: operationType === 'grant' ? '2px solid #1890ff' : '1px solid #d9d9d9',
            }}
          >
            <Radio value="grant">
              <div>
                <div style={{ fontWeight: 500, fontSize: '16px' }}>Grant Permissions</div>
                <div style={{ color: '#666', marginTop: 4 }}>
                  Add new permissions for selected users to access hospitals
                </div>
              </div>
            </Radio>
          </Card>
          <Card
            hoverable
            style={{
              border: operationType === 'revoke' ? '2px solid #1890ff' : '1px solid #d9d9d9',
            }}
          >
            <Radio value="revoke">
              <div>
                <div style={{ fontWeight: 500, fontSize: '16px' }}>Revoke Permissions</div>
                <div style={{ color: '#666', marginTop: 4 }}>
                  Remove existing permissions from selected users
                </div>
              </div>
            </Radio>
          </Card>
          <Card
            hoverable
            style={{
              border: operationType === 'modify' ? '2px solid #1890ff' : '1px solid #d9d9d9',
            }}
          >
            <Radio value="modify">
              <div>
                <div style={{ fontWeight: 500, fontSize: '16px' }}>Modify Permissions</div>
                <div style={{ color: '#666', marginTop: 4 }}>
                  Update permission levels for existing user-hospital access
                </div>
              </div>
            </Radio>
          </Card>
        </Space>
      </Radio.Group>
    </Card>
  )

  // Step 2: Select Users
  const renderUserSelectionStep = () => {
    const userColumns: TableColumnsType<User> = [
      {
        title: 'Name',
        key: 'name',
        render: (_, record) => `${record.first_name} ${record.last_name}`,
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
      },
      {
        title: 'Status',
        dataIndex: 'active',
        key: 'active',
        render: (active: boolean) =>
          active ? <Tag color="green">Active</Tag> : <Tag color="red">Inactive</Tag>,
      },
    ]

    return (
      <Card>
        <div style={{ marginBottom: 16 }}>
          <h3>Select Users</h3>
          <p style={{ color: '#666' }}>
            Search and select users for bulk permission {operationType}
          </p>
        </div>
        <div style={{ marginBottom: 16 }}>
          <Select
            showSearch
            placeholder="Search by name or email"
            style={{ width: '100%' }}
            loading={usersLoading}
            filterOption={false}
            onSearch={setUserSearchTerm}
            mode="multiple"
            value={selectedUsers}
            onChange={setSelectedUsers}
            options={usersData?.data?.map((user: User) => ({
              value: user.id,
              label: `${user.first_name} ${user.last_name} (${user.email})`,
            }))}
          />
        </div>
        {selectedUsers.length > 0 && (
          <>
            <Alert
              message={`${selectedUsers.length} user${selectedUsers.length > 1 ? 's' : ''} selected`}
              type="info"
              style={{ marginBottom: 16 }}
            />
            <Table
              columns={userColumns}
              dataSource={selectedUsersData}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              size="small"
            />
          </>
        )}
      </Card>
    )
  }

  // Step 3: Select Hospitals
  const renderHospitalSelectionStep = () => {
    const hospitalColumns: TableColumnsType<Hospital> = [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: 'City',
        dataIndex: 'city',
        key: 'city',
      },
      {
        title: 'State',
        dataIndex: 'state',
        key: 'state',
      },
      {
        title: 'CMS ID',
        dataIndex: 'cmsid',
        key: 'cmsid',
      },
    ]

    return (
      <Card>
        <div style={{ marginBottom: 16 }}>
          <h3>Select Hospitals</h3>
          <p style={{ color: '#666' }}>
            Search and select hospitals for bulk permission {operationType}
          </p>
        </div>
        <div style={{ marginBottom: 16 }}>
          <Select
            showSearch
            placeholder="Search by hospital name or city (min 3 chars)"
            style={{ width: '100%' }}
            loading={hospitalsLoading}
            filterOption={false}
            onSearch={setHospitalSearchTerm}
            mode="multiple"
            value={selectedHospitals}
            onChange={setSelectedHospitals}
            options={hospitalsData?.data?.map((hospital: Hospital) => ({
              value: hospital.id,
              label: `${hospital.name} - ${hospital.city}, ${hospital.state}`,
            }))}
          />
        </div>
        {selectedHospitals.length > 0 && (
          <>
            <Alert
              message={`${selectedHospitals.length} hospital${selectedHospitals.length > 1 ? 's' : ''} selected`}
              type="info"
              style={{ marginBottom: 16 }}
            />
            <Table
              columns={hospitalColumns}
              dataSource={selectedHospitalsData}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              size="small"
            />
          </>
        )}
      </Card>
    )
  }

  // Step 4: Set Permission Levels (only for grant/modify)
  const renderPermissionLevelsStep = () => (
    <Card>
      <div style={{ marginBottom: 16 }}>
        <h3>Set Permission Levels</h3>
        <p style={{ color: '#666' }}>
          Configure which permission flags to {operationType} for the selected users and hospitals
        </p>
      </div>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div>
          <label style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <input
              type="checkbox"
              checked={permissionLevels.admin}
              onChange={(e) =>
                setPermissionLevels({ ...permissionLevels, admin: e.target.checked })
              }
              style={{ marginRight: 8 }}
            />
            <strong>Admin</strong>
            <span style={{ marginLeft: 8, color: '#666' }}>
              - Full administrative access to the hospital
            </span>
          </label>
        </div>
        <div>
          <label style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <input
              type="checkbox"
              checked={permissionLevels.sender}
              onChange={(e) =>
                setPermissionLevels({ ...permissionLevels, sender: e.target.checked })
              }
              style={{ marginRight: 8 }}
            />
            <strong>Sender</strong>
            <span style={{ marginLeft: 8, color: '#666' }}>
              - Can send patients from this hospital
            </span>
          </label>
        </div>
        <div>
          <label style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <input
              type="checkbox"
              checked={permissionLevels.receiver}
              onChange={(e) =>
                setPermissionLevels({ ...permissionLevels, receiver: e.target.checked })
              }
              style={{ marginRight: 8 }}
            />
            <strong>Receiver</strong>
            <span style={{ marginLeft: 8, color: '#666' }}>
              - Can receive patients at this hospital
            </span>
          </label>
        </div>
        <div>
          <label style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <input
              type="checkbox"
              checked={permissionLevels.readonly}
              onChange={(e) =>
                setPermissionLevels({ ...permissionLevels, readonly: e.target.checked })
              }
              style={{ marginRight: 8 }}
            />
            <strong>Read Only</strong>
            <span style={{ marginLeft: 8, color: '#666' }}>
              - Can view data but not make changes
            </span>
          </label>
        </div>
        <div>
          <label style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <input
              type="checkbox"
              checked={permissionLevels.bi}
              onChange={(e) => setPermissionLevels({ ...permissionLevels, bi: e.target.checked })}
              style={{ marginRight: 8 }}
            />
            <strong>Business Intelligence</strong>
            <span style={{ marginLeft: 8, color: '#666' }}>- Access to BI reports</span>
          </label>
        </div>
        <div>
          <label style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <input
              type="checkbox"
              checked={permissionLevels.dashboard}
              onChange={(e) =>
                setPermissionLevels({ ...permissionLevels, dashboard: e.target.checked })
              }
              style={{ marginRight: 8 }}
            />
            <strong>Dashboard</strong>
            <span style={{ marginLeft: 8, color: '#666' }}>- Access to hospital dashboard</span>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <input
              type="checkbox"
              checked={permissionLevels.teamInsights}
              onChange={(e) =>
                setPermissionLevels({ ...permissionLevels, teamInsights: e.target.checked })
              }
              style={{ marginRight: 8 }}
            />
            <strong>Team Insights</strong>
            <span style={{ marginLeft: 8, color: '#666' }}>
              - Access to team insights dashboard
            </span>
          </label>
        </div>
      </Space>
    </Card>
  )

  // Step 5: Review and Confirm
  const renderReviewStep = () => {
    const totalOperations = selectedUsers.length * selectedHospitals.length

    return (
      <Card>
        <div style={{ marginBottom: 24 }}>
          <h3>Review and Confirm</h3>
          <p style={{ color: '#666' }}>
            Please review the bulk operation before executing. This operation cannot be undone.
          </p>
        </div>

        {executing ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <LoadingOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
            <h3>Executing Bulk Operation...</h3>
            <Progress
              percent={progress}
              status="active"
              style={{ maxWidth: 400, margin: '0 auto' }}
            />
            <p style={{ color: '#666', marginTop: 16 }}>
              {Math.round((progress / 100) * totalOperations)} of {totalOperations} operations
              completed
            </p>
          </div>
        ) : completed ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a', marginBottom: 16 }} />
            <h2 style={{ color: '#52c41a' }}>Operation Completed Successfully!</h2>
            <p style={{ color: '#666', fontSize: '16px', marginTop: 16 }}>
              {totalOperations} permission{totalOperations > 1 ? 's' : ''}{' '}
              {operationType === 'revoke' ? 'revoked' : 'granted'}
            </p>
            <Button type="primary" onClick={handleReset} style={{ marginTop: 24 }}>
              Start New Operation
            </Button>
          </div>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Alert
              message={
                <div>
                  <strong>Operation Summary:</strong>
                  <div style={{ marginTop: 8 }}>
                    This will {operationType} permissions for{' '}
                    <strong>{selectedUsers.length}</strong> user
                    {selectedUsers.length > 1 ? 's' : ''} across{' '}
                    <strong>{selectedHospitals.length}</strong> hospital
                    {selectedHospitals.length > 1 ? 's' : ''}, resulting in{' '}
                    <strong>{totalOperations}</strong> total operation
                    {totalOperations > 1 ? 's' : ''}.
                  </div>
                </div>
              }
              type="warning"
            />

            <div>
              <h4>Operation Type:</h4>
              <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                {operationType.toUpperCase()}
              </Tag>
            </div>

            <div>
              <h4>Selected Users ({selectedUsers.length}):</h4>
              <div style={{ maxHeight: 200, overflow: 'auto', padding: 8, background: '#f5f5f5' }}>
                {selectedUsersData.map((user: User) => (
                  <div key={user.id} style={{ marginBottom: 4 }}>
                    • {user.first_name} {user.last_name} ({user.email})
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h4>Selected Hospitals ({selectedHospitals.length}):</h4>
              <div style={{ maxHeight: 200, overflow: 'auto', padding: 8, background: '#f5f5f5' }}>
                {selectedHospitalsData.map((hospital) => (
                  <div key={hospital.id} style={{ marginBottom: 4 }}>
                    • {hospital.name} - {hospital.city}, {hospital.state}
                  </div>
                ))}
              </div>
            </div>

            {operationType !== 'revoke' && (
              <div>
                <h4>Permission Levels:</h4>
                <Space wrap>
                  {permissionLevels.admin && <Tag color="red">Admin</Tag>}
                  {permissionLevels.sender && <Tag color="blue">Sender</Tag>}
                  {permissionLevels.receiver && <Tag color="green">Receiver</Tag>}
                  {permissionLevels.readonly && <Tag color="default">Read Only</Tag>}
                  {permissionLevels.bi && <Tag color="purple">BI</Tag>}
                  {permissionLevels.dashboard && <Tag color="cyan">Dashboard</Tag>}
                  {permissionLevels.teamInsights && <Tag color="orange">Team Insights</Tag>}
                  {!Object.values(permissionLevels).some((v) => v) && (
                    <span style={{ color: '#999' }}>No permissions selected</span>
                  )}
                </Space>
              </div>
            )}
          </Space>
        )}
      </Card>
    )
  }

  const steps = [
    {
      title: 'Operation Type',
      content: renderOperationTypeStep(),
    },
    {
      title: 'Select Users',
      content: renderUserSelectionStep(),
    },
    {
      title: 'Select Hospitals',
      content: renderHospitalSelectionStep(),
    },
    ...(operationType !== 'revoke'
      ? [
          {
            title: 'Permission Levels',
            content: renderPermissionLevelsStep(),
          },
        ]
      : []),
    {
      title: 'Review & Execute',
      content: renderReviewStep(),
    },
  ]

  const isStepValid = () => {
    switch (currentStep) {
      case 0:
        return true // Operation type always valid
      case 1:
        return selectedUsers.length > 0
      case 2:
        return selectedHospitals.length > 0
      case 3:
        if (operationType === 'revoke') return true
        return Object.values(permissionLevels).some((v) => v)
      case 4:
        return true
      default:
        return false
    }
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1>Bulk Permission Operations</h1>
        <p>Grant or revoke permissions for multiple users and hospitals</p>
      </div>

      <Card style={{ marginBottom: 24 }}>
        <Steps current={currentStep} items={steps.map((step) => ({ title: step.title }))} />
      </Card>

      <div style={{ marginBottom: 24 }}>{steps[currentStep].content}</div>

      {!completed && (
        <Card>
          <Space>
            {currentStep > 0 && !executing && (
              <Button icon={<LeftOutlined />} onClick={handlePrev}>
                Previous
              </Button>
            )}
            {currentStep < steps.length - 1 && (
              <Button
                type="primary"
                icon={<RightOutlined />}
                onClick={handleNext}
                disabled={!isStepValid()}
              >
                Next
              </Button>
            )}
            {currentStep === steps.length - 1 && !executing && (
              <Button type="primary" danger onClick={handleExecute} disabled={!isStepValid()}>
                Execute Bulk Operation
              </Button>
            )}
          </Space>
        </Card>
      )}
    </div>
  )
}
